import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useEditor, useEditingCond } from '@splashthat/splash-cms-engine';
import { useLocalStorage } from '@rehooks/local-storage';
import get from 'lodash/get';

import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
    BRAND_LIBRARY_BUTTON_TEXT,
    BRAND_LIBRARY_HEADLINE_TEXT,
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';
import Logger from 'observability/Logger';
import VirtualSettingsAttendeeApiClient from 'lib/SplashApiClient/subclients/VirtualSettingsAttendeeApiClient'; // eslint-disable-line max-len
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { getInstance as getBootstrap } from 'utils/bootstrap';

import { BlockVariation } from './Embed.styles';
import { NODE_ID_VIRTUAL_CONTENT, EMBED_TYPES } from '../../../constants';
import CustomEmbed from '../../../../components/elements/Embed/CustomEmbed';
import VimeoEmbed from '../../../../components/elements/Embed/VimeoEmbed';
import IntrovokeEmbed from '../../../../components/elements/Embed/IntrovokeEmbed';
import GoldcastEmbedContainer from '../../../../components/elements/Embed/GoldcastEmbed/GoldcastEmbedContainer';
import GoldcastMessage from '../../../../components/elements/Embed/GoldcastEmbed/GoldcastMessage';
import EmbedPlaceholder from './components/EmbedPlaceholder';
import ZoomGatedEmbed from './components/ZoomGatedEmbed';

const LOG = new Logger('Embed');
const RENDER_LOG = new Logger('Embed-Render', { level: 'info' });

// Commented because this piece of code is only used in the commented block below
// const EMBED_LABELS = {
//     [EMBED_TYPES.ZOOM]: 'Zoom',
//     [EMBED_TYPES.CUSTOM]: 'Custom',
//     [EMBED_TYPES.VIMEO]: 'Vimeo',
//     [EMBED_TYPES.INTROVOKE]: 'Splash Studio',
// };

const flags = getBootstrap().get('flags', {});

export default function Embed({ isSecondary, guest, ...props }) {
    const [attempts, setAttempts] = useState(0);
    const { embeds, theme, event } = useTouchpointData();
    // Commented because this piece of code is only used in the commented block below
    // const node = useNode();
    const editor = useEditor((state) => ({
        editing: state.editing,
        nodes: {
            [NODE_ID_VIRTUAL_CONTENT]: state.nodes[NODE_ID_VIRTUAL_CONTENT],
        },
    }));
    const { shouldPreview } = usePreview({ event });
    const embedKey = isSecondary ? 'altEmbedConfig' : 'embedConfig';
    const embedData = embeds.data[embedKey];
    const isVirtualContentTypeLinkOut =
        get(editor.nodes, [NODE_ID_VIRTUAL_CONTENT, 'meta', 'virtualContentType']) === 'link_out';
    const shouldNotRender =
        (isSecondary && (!embeds.data.hasAltEmbed || !embeds.data.shouldRenderAltEmbed)) || isVirtualContentTypeLinkOut;
    const openEmbedModal = useEditingCond(() => embeds.actions.openEmbedModal(embedKey));
    const onClickPlaceholder = () => {
        LOG.info(openEmbedModal);

        if (editor.editing && typeof openEmbedModal === 'function') {
            openEmbedModal();
        } else {
            window.location.reload();
        }
    };
    const [vepTokens] = useLocalStorage(`splashVirtualEvent/${event.id}`);

    /*
    * TEMPORARILY REMOVED UNTIL WE CAN PREVENT THIS FROM TRIGGERING THE UNSAVED CHANGES MODAL
    // Setting displayName when embedData is changed so we reflect the Embed type on the sidebar tools.
    // It is not ideal but as the data structure doesn't allow us to handle this differently ATM is the best approach
    // I found.
    useEffect(() => {
        if (node.id) {
            node.actions.setProp((editedNode) => {
                if (EMBED_LABELS[embedData.type]) {
                    // eslint-disable-next-line no-param-reassign
                    editedNode.displayName = `${EMBED_LABELS[embedData.type]} Embed`;
                }

                return editedNode;
            });
        }
    }, [embedData.type, node.id]);
    */

    // PROD-239806: record the embed type actually rendered to this attendee (keyed by event + contact) so a
    // recurrence of the wrong embed (e.g. Splash Studio/Introvoke instead of Zoom) can be correlated against
    // the type the backend served at check-in.
    // TODO: Remove this once we identify and fix the root cause.
    useEffect(() => {
        if (editor.editing || shouldNotRender) {
            return;
        }
        RENDER_LOG.info('VEP embed rendered', {
            eventId: event?.id,
            abgcId: guest?.id,
            embedType: embedData?.type ?? null,
            isSecondary,
        });
    }, [embedData?.type, isSecondary, shouldNotRender, event?.id, guest?.id, editor.editing]);

    useEffect(() => {
        // only run if condition matches
        if ((!embedData.type || (embedData.type === EMBED_TYPES.CUSTOM && !embedData.embedCode)) && attempts < 2) {
            const runCheckIn = async () => {
                try {
                    LOG.info(`Check-in attempt #${attempts + 1}`);
                    const virtualSettingsAttendeeApiClient = new VirtualSettingsAttendeeApiClient(
                        getSplashApiClient(),
                        vepTokens?.guestAccessHash,
                    );
                    await virtualSettingsAttendeeApiClient.putVirtualEventCheckIn(event.id);
                    setAttempts((prev) => prev + 1);
                } catch (err) {
                    LOG.error('Check-in failed:', err);
                }
            };

            runCheckIn();
        }
    }, [embedData.type, attempts]);

    const getEmbedComponent = () => {
        switch (embedData.type) {
            case EMBED_TYPES.CUSTOM: {
                if (embedData.embedCode) {
                    return <CustomEmbed embedCode={embedData.embedCode} />;
                } else if (attempts >= 2) {
                    LOG.error('Embed data type missing', { embedData });
                    return <EmbedPlaceholder onClick={onClickPlaceholder} editing={editor.editing} />;
                }
                return <div>Loading...</div>;
            }
            case EMBED_TYPES.VIMEO:
                return (
                    <VimeoEmbed
                        videoId={embedData.options?.vimeo?.videoId}
                        supportsChat={embedData.options?.vimeo?.chatEnabled}
                    />
                );
            case EMBED_TYPES.ZOOM:
                return (
                    <ZoomGatedEmbed
                        guest={guest}
                        embedData={embedData}
                        descendants={props.descendants}
                        theme={theme}
                        hovered={props.hovered}
                        selected={props.selected}
                        isContentEditingDisabled={
                            !editor.editing || (editor.editing && !props.selected && !props.hovered)
                        }
                    />
                );
            case EMBED_TYPES.INTROVOKE:
                return (
                    <IntrovokeEmbed
                        introvokeEventId={embedData?.options?.introvoke?.meetingId}
                        userToken={embedData?.options?.introvoke?.userToken}
                        shouldRefreshEmbeds={embeds.data.shouldRefreshEmbeds}
                    />
                );
            case EMBED_TYPES.GOLDCAST: {
                if (editor.editing || shouldPreview) {
                    return (
                        <GoldcastMessage message="Attendees will see the Goldcast embed here during the live event." />
                    );
                }
                return <GoldcastEmbedContainer config={embedData?.options?.goldcast} />;
            }
            default: {
                !editor.editing && LOG.error('Embed data type missing', { embedData });
                return <EmbedPlaceholder onClick={onClickPlaceholder} editing={editor.editing} />;
            }
        }
    };

    if (shouldNotRender) {
        return null;
    }
    return <BlockVariation {...props}>{getEmbedComponent()}</BlockVariation>;
}

Embed.propTypes = {
    isSecondary: PropTypes.bool,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    guest: PropTypes.object,
    descendants: PropTypes.object.isRequired,
};

Embed.defaultProps = {
    isSecondary: false,
    guest: {},
};

Embed.splash = {
    ...BlockVariation.splash,
    related: {
        settings: React.lazy(() => import('./Embed.tools')),
    },
    defaultProps: {
        ...BlockVariation.splash.defaultProps,
        descendants: {
            zoomButton: {
                styles: {
                    fontFamily: BRAND_LIBRARY_BUTTON_TEXT,
                    fontSize: 18,
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontColorHover: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    backgroundColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    backgroundColorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                    borderColor: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    borderColorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                    borderWidth: {
                        top: 1,
                        bottom: 1,
                        left: 1,
                        right: 1,
                    },
                    borderRadius: 4,
                },
                meta: {
                    content: 'Join Now',
                },
            },
            zoomHeadline: {
                styles: {
                    fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
                    fontSize: 24,
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                },
                meta: {
                    content: 'Zoom Meeting',
                },
            },
            ...(flags?.zoomSdkObfTokenEnabled
                ? {
                      zoomEmbedMessage: {
                          styles: {
                              fontFamily: BRAND_LIBRARY_BODY_TEXT,
                              fontSize: 16,
                              fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                          },
                          meta: {
                              content: 'When prompted, sign in to Zoom to enter the virtual room.',
                          },
                      },
                  }
                : {}),
        },
    },
};
